import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatPrice, resolvePhoto } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n/I18nContext";
import { useAutoRefresh } from "@/lib/useAutoRefresh";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PackageOpen, ArrowRight, Package, Truck, Coins, XCircle } from "lucide-react";

const statusColor = {
  pending_seller: "bg-accent/15 text-accent-foreground",
  pending_fee: "bg-accent/15 text-accent-foreground",
  pending_payment: "bg-accent/15 text-accent-foreground",
  awaiting_validation: "bg-accent/15 text-accent-foreground",
  paid: "bg-secondary/10 text-secondary",
  preparing: "bg-primary/10 text-primary",
  shipped: "bg-primary/10 text-primary",
  handed_over: "bg-primary/15 text-primary",
  delivered: "bg-secondary text-secondary-foreground",
  closed: "bg-success/15 text-success",
  refused: "bg-destructive/10 text-destructive",
  disputed: "bg-destructive/10 text-destructive",
  refunded: "bg-muted text-muted-foreground",
  cancelled: "bg-muted text-muted-foreground",
};

export default function Orders() {
  const { user } = useAuth();
  const brand = useBrand();
  const { t } = useT();
  const [orders, setOrders] = useState([]);

  const loadOrders = useCallback(() => { api.get("/orders").then((r) => setOrders(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadOrders(); }, [loadOrders]);
  useAutoRefresh(loadOrders, 15000);

  const isSeller = user.role === "seller";
  const DONE = ["delivered", "closed", "cancelled", "refunded"];
  const current = useMemo(() => orders.filter((o) => !DONE.includes(o.status)), [orders]);
  const history = useMemo(() => orders.filter((o) => DONE.includes(o.status)), [orders]);

  const sellerStats = useMemo(() => {
    const delivered = orders.filter((o) => ["delivered", "closed"].includes(o.status));
    const refused = orders.filter((o) => ["refused", "cancelled"].includes(o.status));
    const revenue = delivered.reduce((s, o) => s + (o.total_amount || 0) + (o.delivery_fee || 0), 0);
    return { total: orders.length, delivered: delivered.length, refused: refused.length, revenue };
  }, [orders]);

  const Kpi = ({ Icon, label, value, tone = "primary" }) => (
    <div className="rounded-2xl bg-card border border-border p-4" data-testid={`sales-kpi-${label}`}>
      <span className={`h-9 w-9 rounded-xl flex items-center justify-center ${tone === "success" ? "bg-success/10 text-success" : tone === "destructive" ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`}>
        <Icon className="h-5 w-5" />
      </span>
      <p className="mt-2.5 font-heading font-extrabold text-2xl text-foreground leading-none">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );

  const Row = (o) => (
    <Link key={o.id} to={`/commande/${o.id}`} data-testid={`order-row-${o.id}`}
      className="rounded-2xl bg-card border border-border p-4 flex items-center gap-4 hover:border-primary hover:shadow-md transition-all">
      <img src={resolvePhoto(o.listing_image)} alt="" className="h-20 w-20 rounded-xl object-cover shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-heading font-semibold text-secondary">{o.listing_title}</h3>
          <Badge className={`border-0 ${statusColor[o.status] || ""}`}>{t(`order.statuses.${o.status}`)}</Badge>
        </div>
        <p className="text-sm text-muted-foreground mt-0.5">
          {isSeller ? `${t("order.buyer")}: ${o.buyer_name}` : `${t("order.seller")}: ${o.seller_name}`} · {o.quantity} × {formatPrice(o.unit_price, brand.currencySymbol)}
        </p>
        {isSeller ? (
          <p className="font-heading font-bold text-success mt-1" data-testid={`net-${o.id}`}>{t("order.netToReceive")}: {formatPrice((o.total_amount || 0) + (o.delivery_fee || 0), brand.currencySymbol)}</p>
        ) : (
          <p className="font-heading font-bold text-primary mt-1">{formatPrice(o.grand_total ?? o.total_amount, brand.currencySymbol)}</p>
        )}
      </div>
      <ArrowRight className="h-5 w-5 text-muted-foreground shrink-0" />
    </Link>
  );

  const List = ({ items, testid }) => items.length === 0 ? (
    <div className="text-center py-16 rounded-2xl bg-card border border-dashed border-border" data-testid={testid}>
      <PackageOpen className="h-10 w-10 mx-auto text-muted-foreground" />
      <p className="mt-4 text-muted-foreground">{t("order.noOrders")}</p>
    </div>
  ) : <div className="grid gap-4">{items.map(Row)}</div>;

  return (
    <div className="container-page py-10">
      <h1 className="font-heading font-extrabold text-3xl text-secondary mb-6">
        {isSeller ? t("order.ordersReceived") : t("order.myOrders")}
      </h1>
      {isSeller && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="sales-dashboard">
          <Kpi Icon={Package} label={t("space.ordersReceived")} value={sellerStats.total} />
          <Kpi Icon={Truck} label={t("space.delivered")} value={sellerStats.delivered} tone="success" />
          <Kpi Icon={XCircle} label={t("space.refused")} value={sellerStats.refused} tone="destructive" />
          <Kpi Icon={Coins} label={t("space.revenue")} value={formatPrice(sellerStats.revenue, brand.currencySymbol)} tone="success" />
        </div>
      )}
      <Tabs defaultValue="current">
        <TabsList>
          <TabsTrigger value="current" data-testid="tab-current-orders">{t("order.currentOrders")} ({current.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history-orders">{t("order.historyOrders")} ({history.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="current" className="mt-6"><List items={current} testid="orders-empty" /></TabsContent>
        <TabsContent value="history" className="mt-6"><List items={history} testid="orders-history-empty" /></TabsContent>
      </Tabs>
    </div>
  );
}
